/**
 * The one sentence a route fallback gets (plan `docs/plans/LOCAL-FIRST-CONNECT.md`
 * §2/D2 step 3, §3/D5).
 *
 * Three moments say the same kind of thing — "this session is on the tunnel, and
 * here is why" — and the app says it **once per hub per session**, never as a
 * stream:
 *
 *   - `tunnel` — the connect path tried the LAN address first and the hub did
 *     not answer there (or its claim refused), so the tunnel took the session
 *     (§2/D2's fallback). This is the sentence a person sees at connect time.
 *   - `lost` — a live local session stopped answering and the tunnel took it
 *     back mid-session (`services/hubLocalRoute.ts` → `fallbackToCloudRoute`).
 *   - `permission` — the browser itself held the LAN request for its
 *     `local-network-access` permission (card `t_4623769a`,
 *     `services/localNetworkAccess.ts`). The LAN address was never *asked* — the
 *     request never reached the wire — so "could not be reached" would be a lie:
 *     the sentence says what the browser is waiting for and the two honest
 *     options (allow it for this site, or continue over the cloud).
 *
 * The notice is a value so every surface paints the same fact in its own way:
 * the launcher shows it in its bottom strip, a hub tab toasts it. The *latch*
 * lives here, and here only, because "once per hub per session" is a property of
 * the statement, not of a surface — two copies of it is how one hub gets told
 * twice.
 */
import { ref } from 'vue';

export type RouteFallbackReason = 'lost' | 'tunnel' | 'permission';

export interface RouteFallbackNotice {
  /** The hub the fallback is about — also the latch key. */
  hubId: string;
  /** Its name as the surface should print it (falls back to the id). */
  hubName: string;
  reason: RouteFallbackReason;
}

/** The fallback the app is showing right now (null = none). */
export const routeFallback = ref<RouteFallbackNotice | null>(null);

/**
 * Hubs already told about in THIS session — keyed by the hub's id, so a hub that
 * flaps does not turn into a stream of notices. Module state on purpose: it is a
 * session fact and must not outlive the app run in storage.
 */
const stated = new Set<string>();

/**
 * State the fallback for one hub. Returns true when this call is the one that
 * said it — the caller may then paint it (a tab toasts it, a view shows it in
 * its own strip), and a second call for the same hub goes quiet.
 */
export function stateRouteFallback(notice: {
  hubId: string;
  hubName?: string | null;
  reason: RouteFallbackReason;
}): boolean {
  const hubId = (notice.hubId ?? '').trim();
  if (!hubId) return false;
  if (stated.has(hubId)) return false;
  stated.add(hubId);
  routeFallback.value = {
    hubId,
    hubName: (notice.hubName ?? '').trim() || hubId,
    reason: notice.reason,
  };
  return true;
}

/** The surface acknowledged the notice. */
export function clearRouteFallback(): void {
  routeFallback.value = null;
}

/** Tests only: a fresh app run. */
export function resetRouteFallbackSession(): void {
  stated.clear();
  routeFallback.value = null;
}

/** Tests only: what has already been stated this session. */
export function routeFallbackStatedCount(): number {
  return stated.size;
}
